export function capsuleHeight(c) {
    return 2 * (c.radius + c.halfHeight);
}
export function capsuleCenter(c) {
    return { x: c.base.x, y: c.base.y + c.radius + c.halfHeight, z: c.base.z };
}
/** Squared distance from a point to the capsule's axis segment. */
export function pointCapsuleAxisDistanceSq(p, c) {
    const y0 = c.base.y + c.radius;
    const y1 = y0 + 2 * c.halfHeight;
    const py = p.y < y0 ? y0 : p.y > y1 ? y1 : p.y;
    const dx = p.x - c.base.x;
    const dy = p.y - py;
    const dz = p.z - c.base.z;
    return dx * dx + dy * dy + dz * dz;
}
/** Distance from a point to the capsule surface (negative inside). */
export function pointCapsuleDistance(p, c) {
    return Math.sqrt(pointCapsuleAxisDistanceSq(p, c)) - c.radius;
}
export function pointInCapsule(p, c) {
    return pointCapsuleAxisDistanceSq(p, c) <= c.radius * c.radius;
}
/** True when a sphere overlaps the capsule. */
export function sphereCapsuleOverlap(center, radius, c) {
    const r = radius + c.radius;
    return pointCapsuleAxisDistanceSq(center, c) <= r * r;
}
function raySphere(origin, dir, cx, cy, cz, r) {
    const lx = origin.x - cx;
    const ly = origin.y - cy;
    const lz = origin.z - cz;
    const b = lx * dir.x + ly * dir.y + lz * dir.z;
    const c = lx * lx + ly * ly + lz * lz - r * r;
    const disc = b * b - c;
    if (disc < 0)
        return null;
    const t = -b - Math.sqrt(disc);
    return t >= 0 ? t : null;
}
/**
 * Distance along the unit `dir` from `origin` to the capsule surface, or null when the ray misses
 * or the surface lies beyond `maxDistance`. A ray that starts inside returns 0.
 */
export function rayCapsule(origin, dir, maxDistance, c) {
    if (pointInCapsule(origin, c))
        return 0;
    const r = c.radius;
    const y0 = c.base.y + r;
    const y1 = y0 + 2 * c.halfHeight;
    const ox = origin.x - c.base.x;
    const oz = origin.z - c.base.z;
    let best = null;
    // The infinite vertical cylinder, then keep the hit only when it lands within the axis band.
    const a = dir.x * dir.x + dir.z * dir.z;
    if (a > 1e-12) {
        const b = ox * dir.x + oz * dir.z;
        const cc = ox * ox + oz * oz - r * r;
        const disc = b * b - a * cc;
        if (disc >= 0) {
            const t = (-b - Math.sqrt(disc)) / a;
            if (t >= 0) {
                const y = origin.y + dir.y * t;
                if (y >= y0 && y <= y1)
                    best = t;
            }
        }
    }
    const bottom = raySphere(origin, dir, c.base.x, y0, c.base.z, r);
    if (bottom !== null && (best === null || bottom < best))
        best = bottom;
    const top = raySphere(origin, dir, c.base.x, y1, c.base.z, r);
    if (top !== null && (best === null || top < best))
        best = top;
    return best !== null && best <= maxDistance ? best : null;
}
/** Fraction 0..1 along the segment a→b at which it enters the capsule, or null. */
export function segmentCapsule(a, b, c) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const dz = b.z - a.z;
    const len = Math.hypot(dx, dy, dz);
    if (len < 1e-9)
        return pointInCapsule(a, c) ? 0 : null;
    const t = rayCapsule(a, { x: dx / len, y: dy / len, z: dz / len }, len, c);
    return t === null ? null : t / len;
}
/**
 * Ray against a yaw-rotated box: distance and world-space surface normal, or null. A ray that
 * starts inside returns distance 0 with the normal facing back along the ray.
 */
export function rayBox(origin, dir, maxDistance, box) {
    const cos = Math.cos(box.yaw);
    const sin = Math.sin(box.yaw);
    // World → local: rotate by -yaw about +Y.
    const wx = origin.x - box.center.x;
    const wz = origin.z - box.center.z;
    const o = [cos * wx - sin * wz, origin.y - box.center.y, sin * wx + cos * wz];
    const d = [cos * dir.x - sin * dir.z, dir.y, sin * dir.x + cos * dir.z];
    const h = [box.halfExtents.x, box.halfExtents.y, box.halfExtents.z];
    let tmin = 0;
    let tmax = maxDistance;
    let axis = -1;
    let sign = 1;
    for (let i = 0; i < 3; i++) {
        const oi = o[i] ?? 0;
        const di = d[i] ?? 0;
        const hi = h[i] ?? 0;
        if (Math.abs(di) < 1e-12) {
            if (Math.abs(oi) > hi)
                return null;
            continue;
        }
        let t1 = (-hi - oi) / di;
        let t2 = (hi - oi) / di;
        let s = -1;
        if (t1 > t2) {
            const tmp = t1;
            t1 = t2;
            t2 = tmp;
            s = 1;
        }
        if (t1 > tmin) {
            tmin = t1;
            axis = i;
            sign = s;
        }
        if (t2 < tmax)
            tmax = t2;
        if (tmin > tmax)
            return null;
    }
    if (axis < 0)
        return { distance: 0, normal: { x: -dir.x, y: -dir.y, z: -dir.z } };
    // Local normal back to world: rotate by +yaw about +Y.
    const lx = axis === 0 ? sign : 0;
    const ly = axis === 1 ? sign : 0;
    const lz = axis === 2 ? sign : 0;
    return {
        distance: tmin,
        normal: { x: cos * lx + sin * lz, y: ly, z: -sin * lx + cos * lz },
    };
}
/** Closest point on a capsule's axis segment to `p`. */
export function closestPointOnAxis(p, c) {
    const y0 = c.base.y + c.radius;
    const y1 = y0 + 2 * c.halfHeight;
    return { x: c.base.x, y: p.y < y0 ? y0 : p.y > y1 ? y1 : p.y, z: c.base.z };
}
/**
 * True when any part of the capsule lies inside a cone: apex, unit direction, half angle, range.
 * The test uses the closest axis point and widens the cone by the capsule radius, so a capsule
 * grazing the cone edge still counts.
 */
export function capsuleInCone(apex, dir, halfAngle, range, c) {
    const q = closestPointOnAxis(apex, c);
    const dx = q.x - apex.x;
    const dy = q.y - apex.y;
    const dz = q.z - apex.z;
    const dist = Math.hypot(dx, dy, dz);
    if (dist - c.radius > range)
        return false;
    if (dist <= c.radius)
        return true;
    const cosAngle = (dx * dir.x + dy * dir.y + dz * dir.z) / dist;
    const angle = Math.acos(cosAngle < -1 ? -1 : cosAngle > 1 ? 1 : cosAngle);
    const widen = Math.asin(Math.min(1, c.radius / dist));
    return angle <= halfAngle + widen;
}
/** True when the two capsules' volumes overlap (planar circles overlap within a shared height band). */
export function capsulesOverlap(a, b) {
    const ha = capsuleHeight(a);
    const hb = capsuleHeight(b);
    if (a.base.y >= b.base.y + hb || b.base.y >= a.base.y + ha)
        return false;
    const r = a.radius + b.radius;
    const dx = a.base.x - b.base.x;
    const dz = a.base.z - b.base.z;
    return dx * dx + dz * dz < r * r;
}
//# sourceMappingURL=geometry.js.map